/**
 * Gruppkraft — grundläggande temabeteenden
 * Mobilmeny, FAQ-accordion, scroll-reveal, sticky CTA, analytics-hjälp
 * Vanilla JS, inga externa beroenden.
 */
(function () {
  "use strict";

  var prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  /* ---------------------------- Analytics helper --------------------------- */
  /**
   * Skickar ett standardiserat event till dataLayer om det finns (GA4/GTM),
   * men aktiverar inget spårningsverktyg i sig. Verktyg ska endast laddas
   * in efter användarens samtycke (se cookiepolicy / samtyckeshantering).
   */
  window.gkTrackEvent = function (eventName, detail) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        window.dataLayer.push(
          Object.assign({ event: eventName }, detail || {})
        );
      }
      document.dispatchEvent(
        new CustomEvent("gk:" + eventName, { detail: detail || {} })
      );
    } catch (err) {
      /* Tyst fel — spårning får aldrig störa användarupplevelsen */
    }
  };

  document.addEventListener("DOMContentLoaded", function () {
    initMobileNav();
    initFaqAccordions();
    initScrollReveal();
    initStickyCta();
    initOutboundTracking();
    initStatCounters();
  });

  /* ------------------------------- Mobile nav ------------------------------- */
  function initMobileNav() {
    var toggle = document.querySelector("[data-nav-toggle]");
    var nav = document.querySelector("[data-mobile-nav]");
    if (!toggle || !nav) return;

    var closeBtn = nav.querySelector("[data-nav-close]");
    var backdrop = nav.querySelector("[data-nav-backdrop]");
    var focusablesSelector =
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function openNav() {
      nav.setAttribute("data-open", "true");
      toggle.setAttribute("aria-expanded", "true");
      document.body.style.overflow = "hidden";
      var first = nav.querySelector(focusablesSelector);
      if (first) first.focus();
      document.addEventListener("keydown", onKeydown);
    }

    function closeNav() {
      nav.setAttribute("data-open", "false");
      toggle.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      toggle.focus();
      document.removeEventListener("keydown", onKeydown);
    }

    function onKeydown(e) {
      if (e.key === "Escape") {
        closeNav();
        return;
      }
      if (e.key === "Tab") {
        var focusables = nav.querySelectorAll(focusablesSelector);
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    toggle.addEventListener("click", function () {
      var isOpen = nav.getAttribute("data-open") === "true";
      isOpen ? closeNav() : openNav();
    });

    if (closeBtn) closeBtn.addEventListener("click", closeNav);
    if (backdrop) backdrop.addEventListener("click", closeNav);
  }

  /* ------------------------------ FAQ accordion ------------------------------ */
  function initFaqAccordions() {
    var groups = document.querySelectorAll("[data-faq-group]");
    groups.forEach(function (group) {
      var allowMultiple = group.getAttribute("data-faq-multiple") === "true";
      var items = group.querySelectorAll("[data-faq-item]");

      items.forEach(function (item) {
        var question = item.querySelector("[data-faq-question]");
        var answer = item.querySelector("[data-faq-answer]");
        if (!question || !answer) return;

        question.addEventListener("click", function () {
          var isOpen = question.getAttribute("aria-expanded") === "true";

          if (!allowMultiple) {
            items.forEach(function (other) {
              if (other === item) return;
              var q = other.querySelector("[data-faq-question]");
              var a = other.querySelector("[data-faq-answer]");
              if (q && a) {
                q.setAttribute("aria-expanded", "false");
                a.style.maxHeight = null;
              }
            });
          }

          question.setAttribute("aria-expanded", String(!isOpen));
          if (!isOpen) {
            answer.style.maxHeight = answer.scrollHeight + "px";
            window.gkTrackEvent("faq_opened", {
              question: question.textContent.trim(),
            });
          } else {
            answer.style.maxHeight = null;
          }
        });
      });
    });
  }

  /* ------------------------------ Scroll reveal ------------------------------ */
  function initScrollReveal() {
    var targets = document.querySelectorAll("[data-reveal]");
    if (!targets.length) return;

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    targets.forEach(function (el, index) {
      el.style.transitionDelay = Math.min(index % 4, 3) * 60 + "ms";
      observer.observe(el);
    });
  }

  /* ---------------------------- Statistik-räknare ----------------------------- */
  /**
   * Räknar upp siffrorna i hero-illustrationens statistikrutor (t.ex.
   * "31 %", "4 mån", "1,2 t") från 0 till slutvärdet när rutan blir synlig,
   * och triggar samtidigt uppritningen av sparkline-grafen/förloppsstapeln
   * (ren CSS-transition på klassen hero-illustration__stat--counted).
   * Talformatet (decimaltecken, suffix) läses av från den befintliga texten
   * så inga extra Theme Editor-inställningar krävs.
   */
  function initStatCounters() {
    var stats = document.querySelectorAll(".hero-illustration__stat");
    if (!stats.length) return;

    if (prefersReducedMotion) {
      stats.forEach(function (stat) {
        stat.classList.add("hero-illustration__stat--counted");
      });
      return;
    }

    function animateValue(el) {
      var raw = el.textContent.trim();
      var match = raw.match(/^(-?[0-9]+(?:[.,][0-9]+)?)(.*)$/);
      if (!match) return;

      var numberText = match[1];
      var suffix = match[2];
      var decimalSep = numberText.indexOf(",") !== -1 ? "," : ".";
      var decimalPart = numberText.split(/[.,]/)[1];
      var decimals = decimalPart ? decimalPart.length : 0;
      var target = parseFloat(numberText.replace(",", "."));
      var duration = 2200;
      var start = null;

      function step(timestamp) {
        if (start === null) start = timestamp;
        var progress = Math.min((timestamp - start) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = target * eased;
        el.textContent = current.toFixed(decimals).replace(".", decimalSep) + suffix;
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          el.textContent = raw;
        }
      }
      window.requestAnimationFrame(step);
    }

    if (!("IntersectionObserver" in window)) {
      stats.forEach(function (stat) {
        stat.classList.add("hero-illustration__stat--counted");
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("hero-illustration__stat--counted");
            var valueEl = entry.target.querySelector("[data-stat-value]");
            if (valueEl) animateValue(valueEl);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    stats.forEach(function (stat) {
      observer.observe(stat);
    });
  }

  /* ------------------------------- Sticky CTA -------------------------------- */
  function initStickyCta() {
    var cta = document.querySelector("[data-sticky-cta]");
    if (!cta) return;

    var hideNear = document.querySelectorAll("[data-hide-sticky-near]");

    function evaluate() {
      var nearBlocked = Array.prototype.some.call(hideNear, function (el) {
        var rect = el.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      });
      cta.setAttribute("data-visible", nearBlocked ? "false" : "true");
    }

    window.addEventListener("scroll", throttle(evaluate, 150), {
      passive: true,
    });
    window.addEventListener("resize", throttle(evaluate, 200));
    evaluate();
  }

  function throttle(fn, wait) {
    var last = 0;
    var timeout = null;
    return function () {
      var now = Date.now();
      var args = arguments;
      if (now - last >= wait) {
        last = now;
        fn.apply(null, args);
      } else {
        clearTimeout(timeout);
        timeout = setTimeout(function () {
          last = Date.now();
          fn.apply(null, args);
        }, wait - (now - last));
      }
    };
  }

  /* --------------------------- Outbound / CTA tracking ------------------------ */
  function initOutboundTracking() {
    document.querySelectorAll("[data-track-cta]").forEach(function (el) {
      el.addEventListener("click", function () {
        window.gkTrackEvent("cta_click", {
          label: el.getAttribute("data-track-cta") || el.textContent.trim(),
        });
      });
    });

    document.querySelectorAll('a[href^="tel:"]').forEach(function (el) {
      el.addEventListener("click", function () {
        window.gkTrackEvent("phone_click", { href: el.getAttribute("href") });
      });
    });

    document.querySelectorAll('a[href^="mailto:"]').forEach(function (el) {
      el.addEventListener("click", function () {
        window.gkTrackEvent("email_click", { href: el.getAttribute("href") });
      });
    });
  }
})();
